import { captureAuthError, renderAuthError } from '@formosa/shared/i18n/authErrorState';
import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { memberApi } from '@formosa/shared/api/member';
import { REMEMBER_USERNAME_KEY } from '@formosa/shared/constants/auth';
import { DEFAULT_REGISTER_CONFIG } from '@formosa/shared/utils/registerConfig';
import AuthModalShell from '../../components/auth/AuthModalShell';
import AuthPhoneCountryFields from '../../components/auth/AuthPhoneCountryFields';
import GoogleSignInButton from '../../components/auth/GoogleSignInButton';
import { useAuth } from '../../auth/AuthContext';
import { useTranslation } from '../../i18n/LocaleContext';

const TABS = {
  EMAIL: 'email',
  MOBILE: 'mobile',
};

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { loginSuccess } = useAuth();
  const { t } = useTranslation();

  const [registerConfig, setRegisterConfig] = useState(DEFAULT_REGISTER_CONFIG);
  const [tab, setTab] = useState(TABS.EMAIL);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [mobile, setMobile] = useState('');
  const [phoneCountry, setPhoneCountry] = useState({
    countryKey: '',
    countryEnName: '',
    phoneAreaCode: '',
  });
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const errorText = renderAuthError(error, t);

  const from = location.state?.from?.pathname || '/';

  const availableTabs = useMemo(() => {
    const tabs = [];
    if (registerConfig.emailLogin) {
      tabs.push(TABS.EMAIL);
    }
    if (registerConfig.phoneLogin) {
      tabs.push(TABS.MOBILE);
    }
    return tabs.length > 0 ? tabs : [TABS.EMAIL];
  }, [registerConfig.emailLogin, registerConfig.phoneLogin]);

  const showLoginTabs = availableTabs.length > 1;

  useEffect(() => {
    let cancelled = false;
    memberApi
      .getRegisterConfig()
      .then((config) => {
        if (!cancelled) {
          setRegisterConfig(config);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!availableTabs.includes(tab)) {
      setTab(availableTabs[0]);
    }
  }, [availableTabs, tab]);

  useEffect(() => {
    const saved = localStorage.getItem(REMEMBER_USERNAME_KEY);
    if (saved) {
      if (saved.startsWith('mobile:')) {
        const [, country, phone] = saved.split(':');
        setPhoneCountry((prev) => ({
          ...prev,
          countryEnName: country || '',
        }));
        setMobile(phone || '');
        if (registerConfig.phoneLogin) {
          setTab(TABS.MOBILE);
        }
      } else {
        setUsername(saved);
        if (registerConfig.emailLogin) {
          setTab(TABS.EMAIL);
        }
      }
      setRememberMe(true);
    }
  }, [registerConfig.emailLogin, registerConfig.phoneLogin]);

  async function persistRemember(loginKey) {
    if (rememberMe) {
      localStorage.setItem(REMEMBER_USERNAME_KEY, loginKey);
    } else {
      localStorage.removeItem(REMEMBER_USERNAME_KEY);
    }
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const payload =
        tab === TABS.MOBILE
          ? {
              username: mobile.trim(),
              password,
              countryName: phoneCountry.countryEnName.trim(),
            }
          : {
              username: username.trim(),
              password,
            };

      const data = await memberApi.login(payload);
      const rememberKey =
        tab === TABS.MOBILE
          ? `mobile:${phoneCountry.countryEnName.trim()}:${mobile.trim()}`
          : username.trim();
      await persistRemember(rememberKey);
      loginSuccess(data);
      navigate(from, { replace: true });
    } catch (err) {
      setError(captureAuthError(err, 'login.loginFailed'));
    } finally {
      setLoading(false);
    }
  }


  return (
    <AuthModalShell mode="login" title={t('login.title')}>
      {error && <div className="auth-alert auth-alert--error">{errorText}</div>}

      {showLoginTabs && (
        <div className="tabs">
          {registerConfig.emailLogin && (
            <button
              type="button"
              className={`tab ${tab === TABS.EMAIL ? 'active' : ''}`}
              onClick={() => setTab(TABS.EMAIL)}
            >
              {t('login.tabEmail')}
            </button>
          )}
          {registerConfig.phoneLogin && (
            <button
              type="button"
              className={`tab ${tab === TABS.MOBILE ? 'active' : ''}`}
              onClick={() => setTab(TABS.MOBILE)}
            >
              {t('login.tabMobile')}
            </button>
          )}
        </div>
      )}

      <form className="auth-form" onSubmit={handleSubmit}>
        {tab === TABS.EMAIL ? (
          <label className="auth-input-wrap">
            <span className="auth-input-icon" aria-hidden>
              <MailIcon />
            </span>
            <input
              className="auth-input"
              type="text"
              placeholder={t('login.usernamePlaceholder')}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username email"
              required
            />
          </label>
        ) : (
          <>
            <AuthPhoneCountryFields
              variant="login"
              idPrefix="login-phone"
              countryKey={phoneCountry.countryKey}
              countryEnName={phoneCountry.countryEnName}
              phoneAreaCode={phoneCountry.phoneAreaCode}
              onChange={setPhoneCountry}
              allowedEnNames={registerConfig.enNames}
              disabled={loading}
            />
            <label className="auth-input-wrap">
              <span className="auth-input-icon" aria-hidden>
                <PhoneIcon />
              </span>
              <input
                className="auth-input"
                type="tel"
                placeholder={t('login.mobilePlaceholder')}
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                autoComplete="tel"
                required
              />
            </label>
          </>
        )}

        <label className="auth-input-wrap">
          <span className="auth-input-icon" aria-hidden>
            <LockIcon />
          </span>
          <input
            className="auth-input"
            type="password"
            placeholder={t('login.passwordPlaceholder')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
          />
        </label>

        <div className="auth-form-row">
          <label className="auth-checkbox">
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
            />
            <span>{t('login.rememberMe')}</span>
          </label>
          {registerConfig.forgotPassword && (
            <Link to="/forgot-password" className="auth-link-muted">
              {t('login.forgotPassword')}
            </Link>
          )}
        </div>

        <button
          type="submit"
          className="auth-btn auth-btn--primary"
          disabled={loading}
        >
          {loading ? t('login.submitting') : t('login.submit')}
        </button>
      </form>


      <div className="auth-divider">
        <span>{t('login.divider')}</span>
      </div>

      <GoogleSignInButton onError={(message) => setError(message ? { key: 'authErrors.generic' } : null)} />
    </AuthModalShell>
  );
}

function MailIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path
        d="M4 6h16a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2z"
        stroke="currentColor"
        strokeWidth="1.6"
      />
      <path d="m4 8 8 5 8-5" stroke="currentColor" strokeWidth="1.6" />
    </svg>
  );
}

function PhoneIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path
        d="M6.5 3h3l1.2 4.5-2 1.2a12 12 0 0 0 5.8 5.8l1.2-2L21.5 14v3a2 2 0 0 1-2.1 2A16.5 16.5 0 0 1 3 6.1 2 2 0 0 1 5 4z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <rect
        x="5"
        y="11"
        width="14"
        height="10"
        rx="2"
        stroke="currentColor"
        strokeWidth="1.6"
      />
      <path
        d="M8 11V8a4 4 0 1 1 8 0v3"
        stroke="currentColor"
        strokeWidth="1.6"
      />
    </svg>
  );
}
